import {
  Brightness4,
  Brightness7,
  Language as LanguageIcon,
  Menu as MenuIcon,
  ArrowDropDown,
} from '@mui/icons-material';
import {
  AppBar,
  Box,
  Button,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  useMediaQuery,
  useTheme as useMuiTheme,
  useScrollTrigger,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';

const Header = () => {
  const { t, i18n } = useTranslation();
  const { mode, toggleTheme } = useTheme();
  const { language, setLanguage, toggleLanguage } = useLanguage();
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.between('sm', 'md'));
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 50,
  });


  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleLanguageMenuOpen = (event) => {
    setLanguageMenuAnchor(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null);
  };

  const handleLanguageChange = (newLanguage) => {
    if (newLanguage !== language) {
      // Use the setLanguage function from context
      setLanguage(newLanguage);
    }
    handleLanguageMenuClose();
  };

  const languageOptions = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'fa', name: 'فارسی', flag: '🇮🇷' },
  ];

  const currentLanguage = languageOptions.find(lang => lang.code === language) || languageOptions[0];

  const menuItems = [
    { label: t('header.menu.home'), href: '/', isRoute: true },
    { label: t('header.menu.allProducts'), href: '/all-products', isRoute: true },
    { label: t('header.menu.aboutUs'), href: '/about', isRoute: true },
    { label: t('header.menu.faq'), href: '/faq', isRoute: true },
    { label: t('header.menu.weblog'), href: '/weblog', isRoute: true },
    { label: t('header.menu.connectWithUs'), href: '/contact', isRoute: true },
  ];

  return (
    <AppBar
      position="fixed"
      elevation={trigger ? 4 : 0}
      sx={{
        backgroundColor: trigger
          ? mode === 'dark'
            ? 'rgba(26, 26, 26, 0.95)'
            : 'rgba(255, 255, 255, 0.95)'
          : 'transparent',
        backdropFilter: trigger ? 'blur(10px)' : 'none',
        transition: 'all 0.3s ease',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Demis Logo */}
            <Box
              component={Link}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
              }}
            >
              <Box
                component="img"
                src="/assets/images/DemisLogoNotext.png"
                alt="Demis Logo"
                sx={{
                  height: { xs: 32, sm: 36, md: 40, lg: 44 },
                  width: 'auto',
                  objectFit: 'contain',
                  transition: 'all 0.3s ease',
                }}
              />
            </Box>
            
            <Box
              component={Link}
              to="/"
              sx={{
                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem', lg: '2rem' },
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #2196f3 30%, #21cbf3 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '0.5px',
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
              }}
            >
              Demis
            </Box>
          </Box>

          {/* Desktop Menu */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {menuItems.map((item, index) => (
                <Button
                  key={index}
                  component={item.isRoute ? Link : 'a'}
                  to={item.isRoute ? item.href : undefined}
                  href={!item.isRoute ? item.href : undefined}
                  sx={{
                    color:
                      location.pathname === item.href
                        ? (mode === 'light' ? 'primary.main' : '#90caf9')
                        : (mode === 'light' ? '#000' : 'inherit'),
                    fontWeight: location.pathname === item.href ? 700 : 500,
                    textTransform: 'none',
                    fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1rem' },
                    px: { xs: 1, sm: 1.5, md: 2 },
                    py: { xs: 0.5, sm: 0.75, md: 1 },
                    textDecoration: 'none',
                    borderRadius: 1,
                    borderBottom: location.pathname === item.href ? `3px solid ${mode === 'light' ? '#1976d2' : '#90caf9'}` : '3px solid transparent',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor:
                        mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.08)'
                          : 'rgba(0, 0, 0, 0.04)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                onClick={handleMobileMenuOpen}
                aria-label="menu"
                sx={{
                  mr: { xs: 0.5, sm: 1 },
                  color: mode === 'light' ? '#000' : 'inherit',
                  p: { xs: 0.75, sm: 1 },
                  '&:hover': {
                    backgroundColor: mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.08)'
                      : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <MenuIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
              </IconButton>
            )}

            {/* Language Dropdown */}
            <Button
              onClick={handleLanguageMenuOpen}
              aria-label="select language"
              endIcon={<ArrowDropDown />}
              startIcon={<LanguageIcon />}
              sx={{
                mx: { xs: 0.25, sm: 0.5 },
                color: mode === 'light' ? '#000' : 'inherit',
                px: { xs: 1, sm: 1.5 },
                py: { xs: 0.75, sm: 1 },
                textTransform: 'none',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: '1rem' }}>
                  {currentLanguage.flag}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    display: { xs: 'none', sm: 'inline' },
                  }}
                >
                  {currentLanguage.name}
                </Typography>
              </Box>
            </Button>

            <IconButton
              onClick={toggleTheme}
              aria-label="toggle theme"
              sx={{
                mx: { xs: 0.25, sm: 0.5 },
                color: mode === 'light' ? '#000' : 'inherit',
                p: { xs: 0.75, sm: 1 },
                '&:hover': {
                  backgroundColor: mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              {mode === 'dark' ?
                <Brightness7 sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} /> :
                <Brightness4 sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
              }
            </IconButton>

          </Box>
        </Toolbar>
      </Container>

      {/* Mobile Menu */}
      <Menu
        anchorEl={mobileMenuAnchor}
        open={Boolean(mobileMenuAnchor)}
        onClose={handleMobileMenuClose}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: mode === 'dark'
              ? 'rgba(26, 26, 26, 0.95)'
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            minWidth: { xs: '180px', sm: '200px' },
            maxWidth: { xs: '280px', sm: '320px' },
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        {menuItems.map((item, index) => (
          <MenuItem
            key={index}
            component={item.isRoute ? Link : 'a'}
            to={item.isRoute ? item.href : undefined}
            href={!item.isRoute ? item.href : undefined}
            onClick={handleMobileMenuClose}
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
              fontWeight: location.pathname === item.href ? 700 : 500,
              py: { xs: 1.25, sm: 1.5 },
              px: { xs: 2, sm: 2.5 },
              textDecoration: 'none',
              color: location.pathname === item.href
                ? (mode === 'light' ? 'primary.main' : '#90caf9')
                : (mode === 'light' ? '#000' : 'inherit'),
              borderLeft: location.pathname === item.href ? `4px solid ${mode === 'light' ? '#1976d2' : '#90caf9'}` : '4px solid transparent',
              borderRadius: 1,
              background:
                location.pathname === item.href
                  ? (mode === 'light'
                      ? 'rgba(33, 150, 243, 0.08)'
                      : 'rgba(144,202,249,0.08)')
                  : 'transparent',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor:
                  mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.04)',
                transform: 'translateX(4px)',
              },
            }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Language Dropdown Menu */}
      <Menu
        anchorEl={languageMenuAnchor}
        open={Boolean(languageMenuAnchor)}
        onClose={handleLanguageMenuClose}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: mode === 'dark'
              ? 'rgba(26, 26, 26, 0.95)'
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            minWidth: '160px',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        {languageOptions.map((option) => (
          <MenuItem
            key={option.code}
            onClick={() => handleLanguageChange(option.code)}
            selected={option.code === language}
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              py: 1.5,
              px: 2,
              color: mode === 'light' ? '#000' : 'inherit',
              borderRadius: 1,
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.04)',
                transform: 'translateX(4px)',
              },
              '&.Mui-selected': {
                backgroundColor: mode === 'dark'
                  ? 'rgba(33, 150, 243, 0.2)'
                  : 'rgba(33, 150, 243, 0.1)',
                '&:hover': {
                  backgroundColor: mode === 'dark'
                    ? 'rgba(33, 150, 243, 0.3)'
                    : 'rgba(33, 150, 243, 0.15)',
                },
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
              <Typography sx={{ fontSize: '1.2rem' }}>
                {option.flag}
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                {option.name}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </AppBar>
  );
};

export default Header;

